import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Label,
  Row,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { withTranslation } from "react-i18next";
import moment from "moment";
import Select from "react-select";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import Breadcrumbs from "components/Common/Breadcrumb";
import { getAllEvents, getAllOrganizer, getEventDetailedRevenueReport } from "api";
import PayoutReceiptModal from "../../../components/PayoutModal/PayoutReceiptModal";

const selectMenuStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
};

const EMPTY_FILTERS = {
  organizerId: "",
  eventId: "",
  eventStatus: "",
};

const AVAILABLE_COLUMNS = [
  { value: "eventId", label: "Event ID" },
  { value: "event", label: "Event (image + title)" },
  { value: "title", label: "Title" },
  { value: "thumbImg", label: "Thumbnail URL" },
  { value: "eventDate", label: "Event date" },
  { value: "eventStatus", label: "Event status" },
  { value: "organizer", label: "Organizer (name + email + phone)" },
  { value: "organizerId", label: "Organizer ID" },
  { value: "organizerCountry", label: "Organizer country" },
  { value: "countryId", label: "Country ID" },
  { value: "countryName", label: "Country name" },
  { value: "countryCode", label: "Country code" },
  { value: "currency", label: "Currency" },
  { value: "timezone", label: "Timezone" },
  { value: "dialingCode", label: "Dialing code" },
  { value: "processingFee", label: "Processing fee (event)" },
  { value: "ticketFee", label: "Ticket fee (event)" },
  { value: "platformFee", label: "Platform fee (event)" },
  { value: "platformFeeType", label: "Platform fee type" },
  { value: "agentFee", label: "Agent fee (event)" },
  { value: "agentFeeType", label: "Agent fee type" },
  { value: "totalTicket", label: "Total tickets" },
  { value: "sumTotalTicketCount", label: "Sum ticket count" },
  { value: "sumTotalTicketAmount", label: "Sum ticket amount" },
  { value: "sumGrandTotal", label: "Sum grand total" },
  { value: "sumDiscountAmount", label: "Sum discount" },
  { value: "sumProcessingFee", label: "Sum processing fee" },
  { value: "sumTicketFee", label: "Sum ticket fee" },
  { value: "sumPlatformFee", label: "Sum platform fee" },
  { value: "sumAgentFee", label: "Sum agent fee" },
  { value: "sumOrganizerRevenue", label: "Sum organizer revenue" },
  { value: "sumEventoxRevenue", label: "Sum Eventox revenue" },
  { value: "sumStripeRevenue", label: "Sum Stripe revenue" },
  { value: "sumDonationAmount", label: "Sum donations" },
];

const DEFAULT_COLUMNS = [
  "eventId",
  "event",
  "eventDate",
  "eventStatus",
  "organizer",
  "organizerCountry",
  "totalTicket",
  "sumGrandTotal",
  "sumOrganizerRevenue",
  "sumEventoxRevenue",
  "sumStripeRevenue",
  "sumPlatformFee",
  "sumAgentFee",
  "processingFee",
  "platformFee",
  "agentFee",
];

const ALL_COLUMN_KEYS = AVAILABLE_COLUMNS.map((c) => c.value);

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "$0.00";
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatNum(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function mapRowForGrid(row) {
  const org = row.organizerDetails || {};
  const country = row.organizerCountryDetails || {};
  const organizerLabel =
    org.name ||
    [org.firstName, org.lastName].filter(Boolean).join(" ") ||
    "—";

  return {
    ...row,
    organizerLabel,
    organizerEmail: org.email || "—",
    organizerPhone: org.phone || "—",
    countryId: country.countryId ?? "—",
    countryName: country.countryName || "—",
    countryCode: country.countryCode || "—",
    currency: country.currency || "—",
    timezone: country.timezone || "—",
    dialingCode: country.dialingCode || "—",
    countryLabel: country.countryName
      ? `${country.countryName}${country.countryCode ? ` (${country.countryCode})` : ""}`
      : "—",
    eventDateLabel: row.eventDate
      ? moment(row.eventDate).format("DD MMM YYYY")
      : "—",
  };
}

function computeSummary(rows) {
  return rows.reduce(
    (acc, r) => {
      acc.events += 1;
      acc.tickets += Number(r.totalTicket) || 0;
      acc.totalAttendees += Number(r.sumTotalTicketCount) || 0;
      acc.grandTotal += Number(r.sumGrandTotal) || 0;
      acc.organizerRevenue += Number(r.sumOrganizerRevenue) || 0;
      acc.eventoxRevenue += Number(r.sumEventoxRevenue) || 0;
      acc.stripeRevenue += Number(r.sumStripeRevenue) || 0;
      return acc;
    },
    {
      events: 0,
      tickets: 0,
      totalAttendees: 0,
      grandTotal: 0,
      organizerRevenue: 0,
      eventoxRevenue: 0,
      stripeRevenue: 0,
    }
  );
}

const EventStatusCell = (params) => {
  const status = String(params.value || "").toLowerCase();
  let color = "secondary";
  if (status.includes("active")) color = "success";
  else if (status.includes("draft")) color = "warning";
  else if (status.includes("close") || status.includes("cancel")) color = "danger";
  return (
    <Badge color={color} className="text-uppercase">
      {params.value || "—"}
    </Badge>
  );
};

const EventCell = (params) => {
  const thumb = params.data?.thumbImg;
  const title = params.data?.title || "—";
  return (
    <div className="d-flex align-items-center gap-2 py-1 h-100">
      {thumb ? (
        <img
          src={thumb}
          alt=""
          style={{
            width: 44,
            height: 44,
            objectFit: "cover",
            borderRadius: 8,
            flexShrink: 0,
          }}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <div
          className="bg-light text-muted d-flex align-items-center justify-content-center"
          style={{ width: 44, height: 44, borderRadius: 8, flexShrink: 0, fontSize: 18 }}
        >
          <i className="bx bx-image" />
        </div>
      )}
      <span className="fw-medium text-truncate" title={title}>
        {title}
      </span>
    </div>
  );
};

const OrganizerCell = (params) => {
  const name = params.data?.organizerLabel || "—";
  const email = params.data?.organizerEmail || "—";
  const phone = params.data?.organizerPhone || "—";
  return (
    <div className="py-1 small lh-sm">
      <div className="fw-semibold text-dark">{name}</div>
      <div className="text-muted">{email}</div>
      <div className="text-muted">{phone}</div>
    </div>
  );
};

const OrganizerCountryCell = (params) => {
  const d = params.data || {};
  if (d.countryName === "—") return <span className="text-muted">—</span>;
  return (
    <div className="py-1 small lh-sm">
      <div className="fw-medium">{d.countryName}</div>
      <div className="text-muted">
        {d.countryCode} · {d.currency}
      </div>
      {d.timezone && d.timezone !== "—" ? (
        <div className="text-muted">{d.timezone}</div>
      ) : null}
    </div>
  );
};

function moneyCol(headerName, field) {
  return {
    headerName,
    field,
    filter: "agNumberColumnFilter",
    sortable: true,
    width: 130,
    valueGetter: (p) => Number(p.data?.[field] ?? 0),
    valueFormatter: (p) => formatMoney(p.value),
  };
}

function textCol(headerName, field, width = 120) {
  return {
    headerName,
    field,
    filter: true,
    sortable: true,
    width,
    valueGetter: (p) => p.data?.[field] ?? "—",
  };
}

const AllEventDetailedReport = (props) => {
  document.title = "All Events Revenue Report | EventoX";

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedColumns, setSelectedColumns] = useState(DEFAULT_COLUMNS);
  const [allEvents, setAllEvents] = useState([]);
  const [organizerOptions, setOrganizerOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const gridRef = useRef(null);

  // Payout states
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const [payoutReceiptData, setPayoutReceiptData] = useState(null);

  const handleGeneratePayout = () => {
    if (!filters.eventId) return;

    const report = rows.find(r => String(r.eventId) === String(filters.eventId));
    if (!report) {
      setError("No revenue report found for the selected event.");
      return;
    }

    try {
      const currency = report.organizerCountryDetails?.currency || report.currency || "USD";
      
      const grossCollected = Number(report.sumGrandTotal) || 0;
      const eventoxFee = Number(report.sumEventoxRevenue) || 0;
      const stripeFee = Number(report.sumStripeRevenue) || 0;
      const combinedFee = eventoxFee + stripeFee;
      const agentFee = Number(report.sumAgentFee) || 0;
      const organizerRevenue = Number(report.sumOrganizerRevenue) || 0;

      const breakdown = [
        { label: "Gross Collected Revenue", amount: grossCollected, type: "income" }
      ];

      if (combinedFee > 0) breakdown.push({ label: "Total Fee", amount: -combinedFee, type: "fee" });
      if (agentFee > 0) breakdown.push({ label: "Agent Fee", amount: -agentFee, type: "fee" });

      if (breakdown.length === 1) {
        breakdown.push({ label: "Fees", amount: 0, type: "fee" });
      }

      const orgName = report.organizerDetails?.name || getFilterLabel("event", filters.eventId);
      const eventName = report.title || getFilterLabel("event", filters.eventId);
      const countryName = report.organizerCountryDetails?.countryName || "";
      const orgDisplayName = countryName ? `${orgName}, ${countryName}` : orgName;

      const receiptData = {
        receipt: {
          number: `EVT-PAYOUT-${new Date().getFullYear()}-${report.eventId}`,
          issued: new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }),
          requested: new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }),
          initiated: new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }),
          status: "Initiated",
          currency,
        },
        organizer: {
          name: orgDisplayName,
          event: eventName,
        },
        breakdown,
        netPayout: {
          amount: organizerRevenue,
          note: "Final amount to be credited to the organizer's bank account.",
        },
        // payoutTo: {
        //   bank: "Organizer Bank Account",
        //   account: "**** **** **** 1234",
        // },
        // timeline: [
        //   { status: "Payout Requested", date: new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }), time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }), completed: true },
        //   { status: "Processing", date: "Pending", time: "-", completed: false },
        //   { status: "Completed", date: "Pending", time: "-", completed: false },
        // ],
      };

      setPayoutReceiptData(receiptData);
      setIsReceiptModalOpen(true);
    } catch (err) {
      console.error("Error generating payout:", err);
      setError("Failed to generate payout receipt for the selected event.");
    }
  };

  const gridRows = useMemo(() => rows.map(mapRowForGrid), [rows]);
  const summary = useMemo(() => computeSummary(rows), [rows]);

  const eventOptions = useMemo(() => {
    let list = allEvents;
    if (filters.organizerId) {
      list = list.filter(
        (e) => String(e.organizerId) === String(filters.organizerId)
      );
    }
    return list.map((item) => ({
      value: item.eventId,
      label: item.eventName || item.title || `Event #${item.eventId}`,
    }));
  }, [allEvents, filters.organizerId]);

  const summaryCards = useMemo(
    () => [
      { title: "Events in report", value: summary.events, icon: "bx-calendar-event", color: "primary" },
      { title: "Bookings", value: summary.tickets.toLocaleString(), icon: "bx-receipt", color: "info" },
      { title: "Total attendees", value: summary.totalAttendees.toLocaleString(), icon: "bx-group", color: "info" },
      { title: "Grand total collected", value: formatMoney(summary.grandTotal), icon: "bx-dollar-circle", color: "success" },
      { title: "Organizer revenue", value: formatMoney(summary.organizerRevenue), icon: "bx-user-circle", color: "warning" },
      { title: "Eventox revenue", value: formatMoney(summary.eventoxRevenue), icon: "bx-buildings", color: "danger" },
      { title: "Stripe fees", value: formatMoney(summary.stripeRevenue), icon: "bx-credit-card", color: "secondary" },
    ],
    [summary]
  );

  const columnDefs = useMemo(() => {
    const cols = [];

    // if (selectedColumns.includes("eventId")) {
    //   cols.push({ headerName: "Event ID", field: "eventId", width: 95, pinned: "left", filter: true, sortable: true });
    // }
    if (selectedColumns.includes("event")) {
      cols.push({
        headerName: "Event",
        field: "title",
        minWidth: 220,
        flex: 1,
        cellRenderer: EventCell,
        filter: true,
        sortable: true,
        autoHeight: true,
        pinned: "left",
      });
    }
    if (selectedColumns.includes("title")) {
      cols.push(textCol("Title", "title", 180));
    }
    if (selectedColumns.includes("thumbImg")) {
      cols.push({ ...textCol("Thumbnail", "thumbImg", 200), tooltipField: "thumbImg" });
    }
    if (selectedColumns.includes("eventDate")) {
      cols.push({ headerName: "Event date", field: "eventDateLabel", width: 120, filter: true, sortable: true });
    }
    if (selectedColumns.includes("eventStatus")) {
      cols.push({
        headerName: "Status",
        field: "eventStatus",
        width: 110,
        cellRenderer: EventStatusCell,
        filter: true,
        sortable: true,
      });
    }
    if (selectedColumns.includes("organizer")) {
      cols.push({
        headerName: "Organizer",
        field: "organizerLabel",
        minWidth: 200,
        cellRenderer: OrganizerCell,
        filter: true,
        sortable: true,
        autoHeight: true,
      });
    }
    if (selectedColumns.includes("organizerId")) {
      cols.push({ headerName: "Organizer ID", field: "organizerId", width: 115, filter: true, sortable: true });
    }
    if (selectedColumns.includes("organizerCountry")) {
      cols.push({
        headerName: "Organizer country",
        field: "countryLabel",
        minWidth: 160,
        cellRenderer: OrganizerCountryCell,
        filter: true,
        sortable: true,
        autoHeight: true,
      });
    }
    if (selectedColumns.includes("countryId")) cols.push(textCol("Country ID", "countryId", 100));
    if (selectedColumns.includes("countryName")) cols.push(textCol("Country name", "countryName", 140));
    if (selectedColumns.includes("countryCode")) cols.push(textCol("Country code", "countryCode", 110));
    if (selectedColumns.includes("currency")) cols.push(textCol("Currency", "currency", 90));
    if (selectedColumns.includes("timezone")) cols.push(textCol("Timezone", "timezone", 150));
    if (selectedColumns.includes("dialingCode")) cols.push(textCol("Dialing code", "dialingCode", 110));

    if (selectedColumns.includes("processingFee")) cols.push(moneyCol("Processing fee", "processingFee"));
    if (selectedColumns.includes("ticketFee")) cols.push(moneyCol("Ticket fee", "ticketFee"));
    if (selectedColumns.includes("platformFee")) cols.push(moneyCol("Platform fee", "platformFee"));
    if (selectedColumns.includes("platformFeeType")) cols.push(textCol("Platform fee type", "platformFeeType", 130));
    if (selectedColumns.includes("agentFee")) cols.push(moneyCol("Agent fee", "agentFee"));
    if (selectedColumns.includes("agentFeeType")) cols.push(textCol("Agent fee type", "agentFeeType", 120));

    if (selectedColumns.includes("totalTicket")) {
      cols.push({
        headerName: "T tickets",
        field: "totalTicket",
        width: 110,
        filter: "agNumberColumnFilter",
        sortable: true,
      });
    }
    if (selectedColumns.includes("sumTotalTicketCount")) {
      cols.push({
        headerName: "T Attendees",
        field: "sumTotalTicketCount",
        width: 130,
        filter: "agNumberColumnFilter",
        sortable: true,
        valueFormatter: (p) => formatNum(p.value),
      });
    }
    if (selectedColumns.includes("sumTotalTicketAmount")) cols.push(moneyCol("T ticket amt", "sumTotalTicketAmount"));
    if (selectedColumns.includes("sumGrandTotal")) cols.push(moneyCol("T grand total", "sumGrandTotal"));
    if (selectedColumns.includes("sumDiscountAmount")) cols.push(moneyCol("T discount amt", "sumDiscountAmount"));
    if (selectedColumns.includes("sumProcessingFee")) cols.push(moneyCol("T processing fee", "sumProcessingFee"));
    if (selectedColumns.includes("sumTicketFee")) cols.push(moneyCol("T ticket fee", "sumTicketFee"));
    if (selectedColumns.includes("sumPlatformFee")) cols.push(moneyCol("T platform fee", "sumPlatformFee"));
    if (selectedColumns.includes("sumAgentFee")) cols.push(moneyCol("Sum agent fee", "sumAgentFee"));
    if (selectedColumns.includes("sumOrganizerRevenue")) cols.push(moneyCol("T organizer rev.", "sumOrganizerRevenue"));
    if (selectedColumns.includes("sumEventoxRevenue")) cols.push(moneyCol("T Eventox rev.", "sumEventoxRevenue"));
    if (selectedColumns.includes("sumStripeRevenue")) cols.push(moneyCol("T Stripe fees", "sumStripeRevenue"));
    if (selectedColumns.includes("sumDonationAmount")) cols.push(moneyCol("T donations", "sumDonationAmount"));

    return cols;
  }, [selectedColumns]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      floatingFilter: true,
    }),
    []
  );

  const loadReport = useCallback(async (filterValues) => {
    setLoading(true);
    setError("");
    try {
      const resp = await getEventDetailedRevenueReport(filterValues);
      if (!resp?.success) {
        throw new Error(resp?.message || "Failed to load report");
      }
      const data = Array.isArray(resp.data) ? resp.data : [];
      setRows(data);
      setCount(resp.count ?? data.length);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Unable to load event revenue report";
      setError(msg);
      setRows([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFilterOptions = useCallback(async () => {
    setOptionsLoading(true);
    try {
      const [eventsResp, organizersResp] = await Promise.all([
        getAllEvents(),
        getAllOrganizer(),
      ]);
      const events = Array.isArray(eventsResp) ? eventsResp : [];
      const organizers = Array.isArray(organizersResp) ? organizersResp : [];
      setAllEvents(events);
      setOrganizerOptions(
        organizers.map((item) => ({
          value: item.organizerId,
          label:
            item.name ||
            [item.firstName, item.lastName].filter(Boolean).join(" ") ||
            `Organizer #${item.organizerId}`,
        }))
      );
      const statuses = [
        ...new Set(
          events
            .map((e) => (e.eventStatus ? String(e.eventStatus).trim() : ""))
            .filter(Boolean)
        ),
      ].sort((a, b) => a.localeCompare(b));
      setStatusOptions(statuses.map((s) => ({ value: s, label: s })));
    } catch (err) {
      console.error("Failed to load filter options:", err);
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    setAppliedFilters(filters);
    loadReport(filters);
  }, [filters, loadReport]);

  const handleOrganizerSelect = (option) => {
    setFilters((prev) => {
      const organizerId = option ? String(option.value) : "";
      let eventId = prev.eventId;
      if (eventId && organizerId) {
        const ev = allEvents.find((e) => String(e.eventId) === String(eventId));
        if (ev && String(ev.organizerId) !== organizerId) eventId = "";
      }
      return { ...prev, organizerId, eventId };
    });
  };

  const handleEventSelect = (option) => {
    setFilters((prev) => ({
      ...prev,
      eventId: option ? String(option.value) : "",
    }));
  };

  const handleStatusSelect = (option) => {
    setFilters((prev) => ({
      ...prev,
      eventStatus: option ? String(option.value) : "",
    }));
  };

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setAppliedFilters(filters);
    loadReport(filters);
  };

  const handleResetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    loadReport(EMPTY_FILTERS);
  };

  const handleColumnSelection = (selectedOptions) => {
    setSelectedColumns(selectedOptions ? selectedOptions.map((o) => o.value) : []);
  };

  const handleClearGridFilters = () => {
    gridRef.current?.api?.setFilterModel(null);
  };

  const handleExportCsv = () => {
    gridRef.current?.api?.exportDataAsCsv({
      fileName: `all-events-revenue-${moment().format("YYYY-MM-DD")}.csv`,
    });
  };

  const selectedOrganizerOption =
    organizerOptions.find(
      (o) => String(o.value) === String(filters.organizerId)
    ) || null;
  const selectedEventOption =
    eventOptions.find((o) => String(o.value) === String(filters.eventId)) ||
    null;
  const selectedStatusOption =
    statusOptions.find((o) => String(o.value) === String(filters.eventStatus)) ||
    null;

  const getFilterLabel = (type, id) => {
    if (!id) return "";
    if (type === "organizer") {
      const o = organizerOptions.find((x) => String(x.value) === String(id));
      return o?.label || id;
    }
    if (type === "event") {
      const e = allEvents.find((x) => String(x.eventId) === String(id));
      return e?.eventName || e?.title || id;
    }
    return id;
  };

  const hasActiveFilters =
    appliedFilters.organizerId ||
    appliedFilters.eventId ||
    appliedFilters.eventStatus;

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title={props.t("Reports")} breadcrumbItem="All Events Revenue Report" />

        <Card className="border-0 shadow-sm mb-4 overflow-hidden">
          <div className="bg-gradient-primary p-4 mb-0">
            <Row className="align-items-center">
              <Col md={8}>
                <h4 className="text-white mb-1 fw-semibold">
                  <i className="bx bx-bar-chart-alt-2 me-2" />
                  All Events Revenue Report
                </h4>
                <p className="text-white-50 mb-0 small">
                  Confirmed primary bookings across every event. Use filters to narrow results.
                </p>
              </Col>
              <Col md={4} className="text-md-end mt-3 mt-md-0">
                <Badge color="light" className="text-primary px-3 py-2">
                  {loading ? "Loading…" : `${count} event${count === 1 ? "" : "s"}`}
                </Badge>
              </Col>
            </Row>
          </div>
        </Card>

        <Card className="shadow-sm border-0 mb-4">
          <CardBody>
            <h5 className="mb-3 text-primary">
              <i className="bx bx-filter-alt me-1" />
              Filters
            </h5>
            <Form onSubmit={handleApplyFilters}>
              <Row className="g-3 align-items-end">
                <Col md={6} lg={3}>
                  <FormGroup className="mb-0">
                    <Label>Organizer</Label>
                    <Select
                      isClearable
                      isLoading={optionsLoading}
                      placeholder="All organizers"
                      value={selectedOrganizerOption}
                      onChange={handleOrganizerSelect}
                      options={organizerOptions}
                      menuPortalTarget={document.body}
                      styles={selectMenuStyles}
                      className="select2-selection"
                    />
                  </FormGroup>
                </Col>
                <Col md={6} lg={3}>
                  <FormGroup className="mb-0">
                    <Label>Event</Label>
                    <Select
                      isClearable
                      isLoading={optionsLoading}
                      placeholder="All events"
                      value={selectedEventOption}
                      onChange={handleEventSelect}
                      options={eventOptions}
                      menuPortalTarget={document.body}
                      styles={selectMenuStyles}
                      className="select2-selection"
                    />
                  </FormGroup>
                </Col>
                <Col md={6} lg={3}>
                  <FormGroup className="mb-0">
                    <Label>Event status</Label>
                    <Select
                      isClearable
                      isLoading={optionsLoading}
                      placeholder="All statuses"
                      value={selectedStatusOption}
                      onChange={handleStatusSelect}
                      options={statusOptions}
                      menuPortalTarget={document.body}
                      styles={selectMenuStyles}
                      className="select2-selection"
                    />
                  </FormGroup>
                </Col>
                <Col md={4} lg={2} className="d-flex gap-2">
                  <Button color="primary" type="submit" disabled={loading} className="flex-grow-1">
                    {loading ? (
                      <Spinner size="sm" />
                    ) : (
                      <>
                        <i className="bx bx-search me-1" />
                        Apply
                      </>
                    )}
                  </Button>
                  <Button color="secondary" outline type="button" onClick={handleResetFilters} disabled={loading}>
                    Reset
                  </Button>
                </Col>
              </Row>
              <div className="d-flex flex-wrap gap-2 mt-2">
                <Button color="success" outline type="button" onClick={handleExportCsv} disabled={!gridRows.length}>
                  <i className="bx bx-download me-1" />
                  Export CSV
                </Button>
                {filters.eventId && (
                  <Button
                    color="info"
                    outline
                    type="button"
                    onClick={handleGeneratePayout}
                  >
                    <i className="bx bx-receipt me-1" /> Generate Payout Receipt
                  </Button>
                )}
              </div>
            </Form>
            {/* {hasActiveFilters ? (
              <p className="text-muted small mb-0 mt-2">
                Active filters:
                {appliedFilters.organizerId
                  ? ` organizer=${getFilterLabel("organizer", appliedFilters.organizerId)}`
                  : ""}
                {appliedFilters.eventId
                  ? ` event=${getFilterLabel("event", appliedFilters.eventId)}`
                  : ""}
                {appliedFilters.eventStatus ? ` status=${appliedFilters.eventStatus}` : ""}
              </p>
            ) : null} */}
          </CardBody>
        </Card>

        {error ? (
          <Alert color="danger" className="shadow-sm">
            <i className="bx bx-error-circle me-2" />
            {error}
          </Alert>
        ) : null}

        <Row className="mb-4">
          {summaryCards.map((card) => (
            <Col xl={2} lg={3} md={4} sm={6} key={card.title} className="mb-3">
              <Card className="mini-stats-wid border-0 shadow-sm h-100">
                <CardBody>
                  <div className="d-flex">
                    <div className="flex-grow-1">
                      <p className="text-muted fw-medium mb-1 small">{card.title}</p>
                      <h5 className="mb-0">{card.value}</h5>
                    </div>
                    <div className={`avatar-sm rounded-circle bg-${card.color} align-self-center`}>
                      <span className={`avatar-title rounded-circle bg-${card.color}`}>
                        <i className={`bx ${card.icon} font-size-20`} />
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>

        <Card className="border-0 shadow-sm">
          <CardBody>
            <Row className="mb-3 align-items-end">
              <Col lg={8}>
                <FormGroup className="mb-0">
                  <Label>Select columns to display</Label>
                  <Select
                    isMulti
                    value={AVAILABLE_COLUMNS.filter((col) => selectedColumns.includes(col.value))}
                    onChange={handleColumnSelection}
                    options={AVAILABLE_COLUMNS}
                    placeholder="Select columns to display"
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menu: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </FormGroup>
              </Col>
              <Col lg={4} className="d-flex flex-wrap gap-2 justify-content-lg-end mt-2 mt-lg-0">
                <Button color="outline-primary" size="sm" type="button" onClick={() => setSelectedColumns(ALL_COLUMN_KEYS)}>
                  Select all
                </Button>
                <Button color="outline-secondary" size="sm" type="button" onClick={() => setSelectedColumns(DEFAULT_COLUMNS)}>
                  Default
                </Button>
                <Button color="warning" size="sm" type="button" onClick={handleClearGridFilters}>
                  <i className="bx bx-reset me-1" />
                  Clear filters
                </Button>
              </Col>
            </Row>

            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Event revenue table</h5>
              <span className="text-muted small">
                {selectedColumns.length} column{selectedColumns.length === 1 ? "" : "s"} visible
              </span>
            </div>

            <div className="ag-theme-quartz" style={{ width: "100%", height: 600 }}>
              <AgGridReact
                ref={gridRef}
                rowData={gridRows}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                pagination
                paginationPageSize={50}
                paginationPageSizeSelector={[25, 50, 100, 200]}
                animateRows
                domLayout="normal"
                enableCellTextSelection
                overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading report…</span>'
                overlayNoRowsTemplate='<span class="ag-overlay-loading-center">No events found for the selected filters</span>'
                loading={loading}
              />
            </div>
          </CardBody>
        </Card>

        <PayoutReceiptModal
          isOpen={isReceiptModalOpen}
          toggle={() => setIsReceiptModalOpen(!isReceiptModalOpen)}
          data={payoutReceiptData}
        />
      </Container>
    </div>
  );
};

export default withTranslation()(AllEventDetailedReport);
